import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronDown } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

const FAQS = [
  {
    q: "Which is the best research lab in Nepal for publishing papers?",
    a: "KhojByte Research Lab is Nepal's leading research publication operating system — an end-to-end consultancy combining mentorship, an LMS, a journal database, plagiarism checks and a publication-workflow dashboard. We help students and faculty target Q1 journals with rigorous, ethical research.",
  },
  {
    q: "Which company in Nepal provides research consulting and publication services?",
    a: "KhojByte Research Lab in Kirtipur, Kathmandu offers full-spectrum research consulting in Nepal — thesis-to-paper conversion, mentor matching, methodology coaching, plagiarism review, journal targeting and submission support — for students, faculty and institutions across South Asia.",
  },
  {
    q: "How can I publish a research paper from Nepal in a Q1 journal?",
    a: "Submit your idea or thesis to KhojByte, get matched with a domain mentor, complete our publication-focused LMS micro-courses, co-write a publication-ready manuscript with the mentor, run an ethical plagiarism review, then submit to a Q1-targeted journal with our cover-letter and response-to-reviewer support.",
  },
  {
    q: "How much does research consulting cost in Nepal?",
    a: "KhojByte offers transparent tiers on /pricing. There is a free tier for browsing ideas, datasets and explainers; Standard adds mentor matching, LMS courses and basic plagiarism checks; Premium adds Q1-journal publication support and full submission consulting.",
  },
  {
    q: "What does KhojByte Research Lab do?",
    a: "We help students, scholars and institutions take a raw idea and turn it into a peer-reviewed, indexed research paper. Our platform combines an LMS, mentor matching, publication-workflow tracking, journal database, ethical plagiarism checks and a researcher portfolio.",
  },
  {
    q: "Who can submit a research idea?",
    a: "Anyone — Bachelor's, Master's, MPhil, PhD candidates, independent researchers and faculty. Once you create an account you can submit an idea, an unfinished thesis, or a class project for feasibility review.",
  },
  {
    q: "How long does the publication process take?",
    a: "A focused thesis-to-paper conversion typically takes 8–14 weeks: 2–3 weeks of restructuring with a mentor, 3–5 weeks of drafting and internal review, then submission with a Q1-targeted journal selection. Review timelines vary by journal.",
  },
  {
    q: "Will I be a co-author on the paper?",
    a: "Yes. Your name appears as the primary author. Mentors are added as co-authors only when their contribution meets ICMJE / journal authorship guidelines. KhojByte itself does not take authorship credit.",
  },
  {
    q: "Do you support Nepali universities and Nepali-language consultations?",
    a: "Yes — we work closely with researchers in Nepal and the South Asian region. Submissions are accepted in English; mentor conversations can happen in Nepali, English or Hindi.",
  },
  {
    q: "How is my research and data protected on KhojByte?",
    a: "All uploads are encrypted in transit and at rest. Mentors and reviewers sign confidentiality terms. You retain full IP. You can request export or deletion at any time — see our Privacy Policy.",
  },
];

export default function FAQ() {
  // Inject FAQ JSON-LD for SEO
  useEffect(() => {
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "kb-faq-jsonld";
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: FAQS.map(({ q, a }) => ({
        "@type": "Question",
        name: q,
        acceptedAnswer: { "@type": "Answer", text: a },
      })),
    });
    document.head.appendChild(script);
    return () => { document.getElementById("kb-faq-jsonld")?.remove(); };
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="faq-page">
      <Navbar />
      <main>
        <section className="relative overflow-hidden border-b border-kb-divider">
          <div aria-hidden className="absolute inset-0 kb-kinetic-hex opacity-50" />
          <div className="relative max-w-4xl mx-auto px-6 lg:px-10 pt-20 pb-12 lg:pt-28">
            <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-purple">FREQUENTLY ASKED</div>
            <h1 className="font-serif text-5xl sm:text-6xl text-kb-ink mt-4 leading-[1.05]">
              Everything you wanted to <em className="italic text-kb-purple">ask</em>.
            </h1>
            <p className="mt-5 text-lg text-kb-ink-muted">
              Answers about our research workflow, mentorship, pricing and privacy.
            </p>
          </div>
        </section>

        <section className="max-w-3xl mx-auto px-6 lg:px-10 py-16">
          <div className="space-y-3" data-testid="faq-list">
            {FAQS.map((f, i) => (
              <details
                key={i}
                className="group kb-card p-5 open:border-kb-purple"
                data-testid={`faq-item-${i}`}
              >
                <summary className="flex items-center justify-between cursor-pointer list-none">
                  <span className="font-serif text-lg text-kb-ink pr-6">{f.q}</span>
                  <ChevronDown className="w-4 h-4 text-kb-purple transition-transform group-open:rotate-180" />
                </summary>
                <p className="mt-4 text-sm text-kb-ink-muted leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </section>

        <section className="bg-kb-subtle border-t border-kb-divider">
          <div className="max-w-3xl mx-auto px-6 lg:px-10 py-16 text-center">
            <h2 className="font-serif text-2xl sm:text-3xl text-kb-ink">Didn't find what you needed?</h2>
            <p className="text-kb-ink-muted mt-3">Drop us a note or chat with our team on WhatsApp.</p>
            <div className="mt-6 flex justify-center gap-3">
              <Link to="/about#contact" className="kb-btn-outline" data-testid="faq-cta-contact">Contact us</Link>
              <Link to="/onboarding/role" className="kb-btn-primary" data-testid="faq-cta-start">
                Get started <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
